// Details-view cache: one IndexedDB record per plate holding the last
// substantive response from every data source, so a re-scan within
// VEHICLE_CACHE_MAX_AGE_DAYS (see cacheConfig.ts) renders instantly offline.
//
// Everything except the AI summary is stored exactly as the APIs return it —
// raw Estonian registry enums included — because display translation happens
// at render time (translateColor & co in soov.ts). A cached record therefore
// follows a live language switch just like freshly fetched data. The AI
// summary is prose generated in one language, so it is cached per language
// under ai[languageTag] and other languages are fetched on demand.
//
// Lives in its own database (not the scan-history one) so the two schemas can
// evolve independently and clearing one never touches the other.

import type { AiCarInsights, AiFuelFigures } from '../api/aiSummary';
import type { Auto24PriceResponse } from '../api/auto24Price';
import type { CarDataResponse } from '../api/carData';
import type { CarTaxResponse } from '../api/carTax';
import type { MntResponse } from '../api/checkVehicle';
import type { LatvianRegCheckResponse } from '../api/latvianRegCheck';
import { VEHICLE_CACHE_MAX_AGE_MS } from './cacheConfig';

const DB_NAME = 'autospy-details-cache';
const STORE = 'vehicles';

export interface CachedSlice<T> {
  data: T;
  /** epoch ms */
  fetchedAt: number;
}

export interface VehicleCacheRecord {
  /** sanitized registration number, the primary key */
  plate: string;
  /** epoch ms of the last write */
  updatedAt: number;
  mnt?: CachedSlice<MntResponse>;
  latvian?: CachedSlice<LatvianRegCheckResponse>;
  carData?: CachedSlice<CarDataResponse>;
  tax?: CachedSlice<CarTaxResponse>;
  price?: CachedSlice<Auto24PriceResponse>;
  /** app language tag → AI summary in that language */
  ai?: Record<string, CachedSlice<AiCarInsights>>;
  /** AI-estimated fuel economy (numbers — language-independent, one slice) */
  aiFuel?: CachedSlice<AiFuelFigures>;
}

export type VehicleCachePatch = Partial<Omit<VehicleCacheRecord, 'plate' | 'updatedAt'>>;

export function isFresh<T>(
  slice: CachedSlice<T> | undefined,
  now: number = Date.now(),
  maxAgeMs: number = VEHICLE_CACHE_MAX_AGE_MS,
): slice is CachedSlice<T> {
  return slice !== undefined && now - slice.fetchedAt < maxAgeMs;
}

/**
 * Drops slices older than maxAgeMs. Returns the record unchanged (same
 * reference) when nothing expired, and null when no fresh slice remains.
 */
export function pruneExpired(
  record: VehicleCacheRecord,
  now: number = Date.now(),
  maxAgeMs: number = VEHICLE_CACHE_MAX_AGE_MS,
): VehicleCacheRecord | null {
  let dropped = false;
  const pruned: VehicleCacheRecord = { plate: record.plate, updatedAt: record.updatedAt };

  function keep<T>(slice: CachedSlice<T> | undefined, assign: (fresh: CachedSlice<T>) => void): void {
    if (slice === undefined) return;
    if (isFresh(slice, now, maxAgeMs)) assign(slice);
    else dropped = true;
  }

  keep(record.mnt, (s) => (pruned.mnt = s));
  keep(record.latvian, (s) => (pruned.latvian = s));
  keep(record.carData, (s) => (pruned.carData = s));
  keep(record.tax, (s) => (pruned.tax = s));
  keep(record.price, (s) => (pruned.price = s));
  keep(record.aiFuel, (s) => (pruned.aiFuel = s));

  const ai: Record<string, CachedSlice<AiCarInsights>> = {};
  let aiCount = 0;
  for (const [lang, slice] of Object.entries(record.ai ?? {})) {
    if (isFresh(slice, now, maxAgeMs)) {
      ai[lang] = slice;
      aiCount += 1;
    } else {
      dropped = true;
    }
  }
  if (aiCount > 0) pruned.ai = ai;

  if (!dropped) return record;
  if (
    !pruned.mnt &&
    !pruned.latvian &&
    !pruned.carData &&
    !pruned.tax &&
    !pruned.price &&
    !pruned.ai &&
    !pruned.aiFuel
  ) {
    return null;
  }
  return pruned;
}

/** Merges a patch into an existing record; AI slices merge per language. */
export function mergeRecord(
  existing: VehicleCacheRecord | null,
  plate: string,
  patch: VehicleCachePatch,
  now: number = Date.now(),
): VehicleCacheRecord {
  const ai = { ...existing?.ai, ...patch.ai };
  const merged: VehicleCacheRecord = { ...existing, ...patch, plate, updatedAt: now };
  if (Object.keys(ai).length > 0) merged.ai = ai;
  else delete merged.ai;
  return merged;
}

// ---- IndexedDB plumbing ----

function cacheAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'plate' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('details cache open failed'));
  });
}

/**
 * Fresh slices for a plate, or null when nothing usable is cached. Expired
 * slices are garbage-collected in the same transaction: they would never be
 * read again, only overwritten.
 */
export function readVehicleCache(plate: string, now: number = Date.now()): Promise<VehicleCacheRecord | null> {
  if (!cacheAvailable()) return Promise.resolve(null);
  return openDb().then(
    (db) =>
      new Promise<VehicleCacheRecord | null>((resolve, reject) => {
        const t = db.transaction(STORE, 'readwrite');
        const store = t.objectStore(STORE);
        const req = store.get(plate);
        let result: VehicleCacheRecord | null = null;
        req.onsuccess = () => {
          const record = req.result as VehicleCacheRecord | undefined;
          if (!record) return;
          result = pruneExpired(record, now);
          if (result === null) store.delete(plate);
          else if (result !== record) store.put(result);
        };
        t.oncomplete = () => {
          db.close();
          resolve(result);
        };
        t.onabort = t.onerror = () => {
          db.close();
          reject(t.error ?? new Error('details cache read failed'));
        };
      }),
  );
}

/**
 * Atomically merges the patch into the plate's record (get + put in one
 * readwrite transaction, so parallel source completions can't lose writes).
 */
export function writeVehicleCache(plate: string, patch: VehicleCachePatch, now: number = Date.now()): Promise<void> {
  if (!cacheAvailable()) return Promise.resolve();
  return openDb().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const t = db.transaction(STORE, 'readwrite');
        const store = t.objectStore(STORE);
        const req = store.get(plate);
        req.onsuccess = () => {
          const existing = (req.result as VehicleCacheRecord | undefined) ?? null;
          store.put(mergeRecord(existing, plate, patch, now));
        };
        t.oncomplete = () => {
          db.close();
          resolve();
        };
        t.onabort = t.onerror = () => {
          db.close();
          reject(t.error ?? new Error('details cache write failed'));
        };
      }),
  );
}
